import type { Translate } from "@/lib/i18n/translate";
import type { TaskStatusPhase } from "@v1/backend/logic/types";
import { cn } from "@v1/ui/utils";
import { useTranslations } from "next-intl";
import type { ReactElement } from "react";

export type PortalTaskStatus = TaskStatusPhase | null;

interface PortalStatusBadgeStyle {
  readonly label: string;
  readonly dotClassName: string;
  readonly textClassName: string;
  readonly bgClassName: string;
}

function resolveStyle(
  t: Translate,
  status: PortalTaskStatus,
): PortalStatusBadgeStyle {
  if (status === "done") {
    return {
      label: t("erledigt"),
      dotClassName: "bg-primary/30",
      textClassName: "text-primary/55",
      bgClassName: "bg-action-500/[0.05]",
    };
  }
  if (status === "active") {
    return {
      label: t("bearbeitung"),
      dotClassName: "bg-amber-500",
      textClassName: "text-amber-700",
      bgClassName: "bg-amber-500/10",
    };
  }
  // "new", null, or an unexpected value → the calm "Offen" default rather than
  // silently guessing "In Bearbeitung".
  return {
    label: t("offen"),
    dotClassName: "bg-action-500",
    textClassName: "text-action-700",
    bgClassName: "bg-action-500/10",
  };
}

export function PortalStatusBadge({
  status,
  size = "sm",
}: {
  readonly status: PortalTaskStatus;
  readonly size?: "sm" | "md";
}): ReactElement {
  const t = useTranslations("portal.statusBadge");
  const style = resolveStyle(t, status);
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full font-medium leading-tight",
        style.bgClassName,
        style.textClassName,
        size === "sm" ? "h-6 px-2.5 text-[11px]" : "h-7 px-3 text-[12px]",
      )}
    >
      <span className={cn("size-1.5 rounded-full", style.dotClassName)} />
      {style.label}
    </span>
  );
}
